import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import RoutineBuilder from './components/RoutineBuilder';
import Calendar from './components/Calendar';
import Library from './components/Library';
import Settings from './components/Settings';
import Demo from './components/Demo';
import SafeIcon from './common/SafeIcon';
import FitSyncLogo from './components/FitSyncLogo';
import { FiUser, FiClock, FiActivity } from 'react-icons/fi';

export default function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [time, setTime] = useState(new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute:'2-digit', second:'2-digit' }));

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute:'2-digit', second:'2-digit' }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <Dashboard />;
      case 'demo': return <Demo />;
      case 'builder': return <RoutineBuilder />;
      case 'calendar': return <Calendar />;
      case 'library': return <Library />;
      case 'settings': return <Settings />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-carbon-900 text-gray-200 font-sans antialiased selection:bg-lime-500 selection:text-carbon-900 flex flex-col md:flex-row overflow-hidden relative">
      {/* Visual Accents */}
      <div className="fixed top-[-10%] left-[-10%] w-[50%] h-[50%] bg-lime-500/5 rounded-full blur-[140px] pointer-events-none z-0" />
      <div className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none z-0" />
      <div className="fixed inset-0 bg-grid-pattern opacity-[0.4] pointer-events-none z-0" />

      <Navigation 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="flex-1 flex flex-col h-screen overflow-hidden relative z-10 pt-16 md:pt-0">
        <header className="h-20 px-6 md:px-10 flex items-center justify-between border-b border-carbon-800/50 bg-carbon-900/60 backdrop-blur-xl z-20 shrink-0">
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <FitSyncLogo className="w-7 h-7" />
            </div>
            <h1 className="text-sm md:text-base font-bold text-white uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="text-gray-600 font-mono text-[10px] hidden sm:inline">FS_SYSTEM //</span>
              <span className="text-glow">
                {activeTab === 'dashboard' ? 'TELEMETRY' : 
                 activeTab === 'demo' ? 'OPERATIONS_HUB' : 
                 activeTab === 'builder' ? 'CONSTRUCTOR' : activeTab.replace('-', '_').toUpperCase()}
              </span>
            </h1>
          </div>
          
          <div className="flex items-center gap-4 md:gap-8">
            <div className="hidden sm:flex items-center gap-3 text-lime-500 font-mono text-[11px] bg-carbon-800/80 px-4 py-2 rounded-xl border border-carbon-700/50 shadow-2xl uppercase tracking-widest">
              <div className="w-1.5 h-1.5 rounded-full bg-lime-500 animate-pulse" />
              {time}
            </div>
            <div className="flex items-center gap-3 bg-carbon-800/80 py-1.5 pl-1.5 pr-4 rounded-full border border-carbon-700/50 cursor-pointer hover:border-lime-500/50 transition-all duration-300 group shadow-lg">
              <div className="w-8 h-8 rounded-full bg-carbon-700 border border-carbon-600 flex items-center justify-center text-gray-400 group-hover:text-lime-500 group-hover:border-lime-500/30 transition-all">
                <SafeIcon icon={FiUser} />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] font-mono font-bold text-white uppercase tracking-wider">Operative_01</span>
                <span className="text-[8px] font-mono text-gray-500 uppercase">Level_7</span>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 relative scrollbar-hide">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 15, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -15, filter: 'blur(10px)' }}
              transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
              className="h-full max-w-7xl mx-auto w-full"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}